/**
 * <s-product-gallery> — lightweight product media slider.
 *
 * - Native CSS scroll-snap does the sliding, so touch swipe is free and smooth.
 * - Mouse users can click and drag the image (snaps to the nearest slide on release).
 * - Prev/next arrows and thumbnails jump to a slide; the active thumbnail follows
 *   the visible slide and is kept in view inside the rail.
 * - Arrow keys move between slides when the track has focus.
 * - [data-gallery-open] opens a full screen lightbox ([data-gallery-dialog], a native <dialog>):
 *   slides side by side with the same swipe / mouse-drag sliding as the gallery, round arrows,
 *   thumbnails below, arrow keys, Escape or the close button.
 *
 * Required child markup:
 *   [data-gallery-track]  — horizontal scroll container holding the slides.
 *   [data-gallery-slide]  — each slide (full track width).
 * Optional:
 *   [data-gallery-prev] / [data-gallery-next] — arrow buttons.
 *   [data-gallery-thumbs]                      — thumbnail rail container.
 *   [data-gallery-thumb="<index>"]             — thumbnail buttons (aria-current marks the active one).
 *   [data-media-id] on slides                  — matched against [data-selected-variant-media] inside
 *                                                [data-product-form] to jump to the selected variant image.
 */
class SProductGallery extends HTMLElement {
  #ac;
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement[]} */
  #slides = [];
  /** @type {HTMLElement[]} */
  #thumbs = [];
  #index = 0;
  #raf = 0;
  #lastMedia = '';
  /** @type {Animation | null} */
  #fade = null;
  #drag = { active: false, moved: false, startX: 0, startScroll: 0, pointerId: -1 };

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;
    this.#slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.#thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.#setupLightbox(opts);
    if (this.#slides.length < 2) return;

    const track = this.#track;

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(this.#index - 1), opts);
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(this.#index + 1), opts);

    for (const thumb of this.#thumbs) {
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.galleryThumb)), opts);
    }

    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(this.#raf);
        this.#raf = requestAnimationFrame(() => this.#syncFromScroll());
      },
      { ...opts, passive: true }
    );

    track.addEventListener(
      'keydown',
      (event) => {
        const rtl = getComputedStyle(track).direction === 'rtl';
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.goTo(this.#index + (rtl ? -1 : 1));
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.#index + (rtl ? 1 : -1));
        }
      },
      opts
    );

    // Mouse drag (touch and pen already scroll natively).
    track.addEventListener('pointerdown', (event) => this.#onPointerDown(event), opts);
    track.addEventListener('pointermove', (event) => this.#onPointerMove(event), opts);
    track.addEventListener('pointerup', (event) => this.#onPointerUp(event), opts);
    track.addEventListener('pointercancel', (event) => this.#onPointerUp(event), opts);
    // Swallow the click that follows a drag so links/videos are not triggered.
    track.addEventListener(
      'click',
      (event) => {
        if (this.#drag.moved) {
          event.preventDefault();
          event.stopPropagation();
          this.#drag.moved = false;
        }
      },
      { ...opts, capture: true }
    );

    window.addEventListener('resize', () => this.goTo(this.#index, 'instant'), opts);

    // Touch swipe past the first / last slide loops around (native scroll can't go further).
    let touchStartX = 0;
    let touchStartIndex = 0;
    track.addEventListener(
      'touchstart',
      (event) => {
        touchStartX = event.touches[0]?.clientX ?? 0;
        touchStartIndex = this.#index;
      },
      { ...opts, passive: true }
    );
    track.addEventListener(
      'touchend',
      (event) => {
        const dx = (event.changedTouches[0]?.clientX ?? 0) - touchStartX;
        const rtl = getComputedStyle(track).direction === 'rtl';
        const forward = rtl ? dx > 50 : dx < -50;
        const backward = rtl ? dx < -50 : dx > 50;
        const last = this.#slides.length - 1;
        if (forward && touchStartIndex === last && this.#index === last) this.goTo(last + 1);
        else if (backward && touchStartIndex === 0 && this.#index === 0) this.goTo(-1);
      },
      { ...opts, passive: true }
    );

    // Follow the selected variant.
    // 1) Instantly: the gallery's own variant→media map is read the moment an option changes.
    // 2) Fallback: the product form partial (re-rendered by the variant picker) carries the media id.
    /** @type {Record<string, string>} */
    let variantMedia = {};
    try {
      variantMedia = JSON.parse(this.querySelector('[data-gallery-variant-media]')?.textContent || '{}');
    } catch {
      variantMedia = {};
    }
    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        let variantId = '';
        if (target instanceof HTMLInputElement && target.dataset.variantId) variantId = target.dataset.variantId;
        else if (target instanceof HTMLSelectElement) variantId = target.selectedOptions[0]?.dataset.variantId ?? '';
        const media = variantId ? variantMedia[variantId] : '';
        if (media) this.#showMedia(media);
      },
      opts
    );

    const form = document.querySelector('[data-product-form]');
    if (form) {
      this.#lastMedia = form.querySelector('[data-selected-variant-media]')?.getAttribute('data-selected-variant-media') ?? '';
      const observer = new MutationObserver(() => {
        const media = form.querySelector('[data-selected-variant-media]')?.getAttribute('data-selected-variant-media') ?? '';
        if (media) this.#showMedia(media);
      });
      observer.observe(form, { childList: true, subtree: true });
      this.#ac.signal.addEventListener('abort', () => observer.disconnect());
      // A shared ?variant= link opens on that variant's image.
      const initial = this.#slides.findIndex((slide) => slide.dataset.mediaId === this.#lastMedia);
      if (initial > 0) requestAnimationFrame(() => this.goTo(initial, 'instant'));
    }

    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#raf);
  }

  /**
   * @param {number} index
   * @param {ScrollBehavior} [behavior]
   */
  goTo(index, behavior) {
    if (!this.#track) return;
    const count = this.#slides.length;
    // Loop: past the last slide goes to the first, before the first goes to the last.
    if (index >= count || index < 0) {
      this.#jumpTo(((index % count) + count) % count);
      return;
    }
    const slide = this.#slides[index];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({
      left: slide.offsetLeft - this.#track.offsetLeft,
      behavior: behavior ?? (reduce ? 'instant' : 'smooth'),
    });
    this.#index = index;
    this.#update();
  }

  /** Show the slide for a media id (used by variant changes); ignores repeats. @param {string} media */
  #showMedia(media) {
    if (media === this.#lastMedia) return;
    this.#lastMedia = media;
    const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === media);
    if (index !== -1 && index !== this.#index) this.#jumpTo(index);
  }

  /**
   * Switch to a slide without scrolling through the ones in between:
   * a quick fade out, an instant jump, then a fade in.
   * @param {number} index
   */
  #jumpTo(index) {
    const track = this.#track;
    if (!track) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || typeof track.animate !== 'function') {
      this.goTo(index, 'instant');
      return;
    }
    this.#fade?.cancel();
    const fadeOut = track.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 90, easing: 'ease-out', fill: 'forwards' });
    this.#fade = fadeOut;
    // Highlight the target thumbnail right away so the click feels instant.
    this.#index = index;
    this.#update();
    fadeOut.finished
      .then(() => {
        track.style.scrollSnapType = 'none';
        this.goTo(index, 'instant');
        track.style.scrollSnapType = '';
        const fadeIn = track.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 180, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' });
        fadeOut.cancel();
        this.#fade = fadeIn;
      })
      .catch(() => {});
  }

  #syncFromScroll() {
    if (!this.#track || this.#drag.active) return;
    const width = this.#track.clientWidth || 1;
    const index = Math.round(Math.abs(this.#track.scrollLeft) / width);
    if (index !== this.#index) {
      this.#index = Math.max(0, Math.min(this.#slides.length - 1, index));
      this.#update();
    }
  }

  #update() {
    // Arrows stay enabled: the gallery loops.

    this.#thumbs.forEach((thumb, i) => {
      if (i === this.#index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    this.#revealActiveThumb();
  }

  /** Keep the active thumbnail visible inside the rail without scrolling the page. */
  #revealActiveThumb() {
    const rail = this.querySelector('[data-gallery-thumbs]');
    const thumb = this.#thumbs[this.#index];
    if (!(rail instanceof HTMLElement) || !thumb) return;
    const vertical = rail.scrollHeight > rail.clientHeight + 1 && getComputedStyle(rail).flexDirection === 'column';
    if (vertical) {
      const top = thumb.offsetTop - rail.offsetTop;
      if (top < rail.scrollTop) rail.scrollTo({ top, behavior: 'smooth' });
      else if (top + thumb.offsetHeight > rail.scrollTop + rail.clientHeight) {
        rail.scrollTo({ top: top + thumb.offsetHeight - rail.clientHeight, behavior: 'smooth' });
      }
    } else if (rail.scrollWidth > rail.clientWidth + 1) {
      const left = thumb.offsetLeft - rail.offsetLeft;
      if (left < rail.scrollLeft) rail.scrollTo({ left, behavior: 'smooth' });
      else if (left + thumb.offsetWidth > rail.scrollLeft + rail.clientWidth) {
        rail.scrollTo({ left: left + thumb.offsetWidth - rail.clientWidth, behavior: 'smooth' });
      }
    }
  }

  /** @param {PointerEvent} event */
  #onPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0 || !this.#track) return;
    if (event.target instanceof Element && event.target.closest('video, iframe, button')) return;
    this.#drag = {
      active: true,
      moved: false,
      startX: event.clientX,
      startScroll: this.#track.scrollLeft,
      pointerId: event.pointerId,
    };
    this.#track.style.scrollSnapType = 'none';
    this.#track.style.cursor = 'grabbing';
  }

  /** @param {PointerEvent} event */
  #onPointerMove(event) {
    if (!this.#drag.active || event.pointerId !== this.#drag.pointerId || !this.#track) return;
    const dx = event.clientX - this.#drag.startX;
    if (!this.#drag.moved && Math.abs(dx) > 4) {
      this.#drag.moved = true;
      this.#track.setPointerCapture(event.pointerId);
    }
    if (this.#drag.moved) this.#track.scrollLeft = this.#drag.startScroll - dx;
  }

  /** @param {PointerEvent} event */
  #onPointerUp(event) {
    if (!this.#drag.active || event.pointerId !== this.#drag.pointerId || !this.#track) return;
    const dx = event.clientX - this.#drag.startX;
    this.#drag.active = false;
    if (this.#track.hasPointerCapture(event.pointerId)) this.#track.releasePointerCapture(event.pointerId);
    this.#track.style.cursor = '';

    let target = this.#index;
    const threshold = Math.min(80, this.#track.clientWidth * 0.15);
    const rtl = getComputedStyle(this.#track).direction === 'rtl';
    if (dx < -threshold) target += rtl ? -1 : 1;
    else if (dx > threshold) target += rtl ? 1 : -1;

    // Animate from the dragged position, then hand control back to scroll-snap.
    this.goTo(target);
    window.setTimeout(() => {
      if (this.#track && !this.#drag.active) this.#track.style.scrollSnapType = '';
    }, 400);
  }

  /* ---------- Full screen lightbox ---------- */

  /**
   * Lightbox state. Slides sit side by side in [data-lightbox-track] (scroll-snap), exactly like the page
   * gallery: touch swipe is native, mouse drag / arrows / thumbnails / arrow keys scroll the track.
   */
  #lb = {
    /** @type {HTMLDialogElement | null} */ dialog: null,
    /** @type {HTMLElement | null} */ track: null,
    /** @type {HTMLElement[]} */ slides: [],
    /** @type {HTMLElement[]} */ thumbs: [],
    index: 0,
    raf: 0,
    drag: { active: false, moved: false, startX: 0, startScroll: 0, pointerId: -1 },
  };

  /** Wire the [data-gallery-open] button and the [data-gallery-dialog] lightbox. @param {AddEventListenerOptions} opts */
  #setupLightbox(opts) {
    const dialog = this.querySelector('[data-gallery-dialog]');
    const openButton = this.querySelector('[data-gallery-open]');
    const track = dialog?.querySelector('[data-lightbox-track]');
    if (!(dialog instanceof HTMLDialogElement) || !openButton || !(track instanceof HTMLElement)) return;
    const lb = this.#lb;
    lb.dialog = dialog;
    lb.track = track;
    lb.slides = [...dialog.querySelectorAll('[data-lightbox-slide]')];
    lb.thumbs = [...dialog.querySelectorAll('[data-lightbox-thumb]')];

    openButton.addEventListener('click', () => this.#openLightbox(), opts);
    dialog.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.#closeLightbox(), opts);

    // Desktop card: a click on the dimmed backdrop (outside the card) lands on the dialog itself and closes it.
    dialog.addEventListener('click', (event) => event.target === dialog && this.#closeLightbox(), opts);

    // Escape: play the closing animation instead of closing instantly.
    dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.#closeLightbox();
      },
      opts
    );
    dialog.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.#showLightbox(lb.index - 1), opts);
    dialog.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.#showLightbox(lb.index + 1), opts);
    for (const thumb of lb.thumbs) {
      thumb.addEventListener('click', () => this.#showLightbox(Number(thumb.dataset.lightboxThumb)), opts);
    }

    dialog.addEventListener(
      'keydown',
      (event) => {
        const rtl = getComputedStyle(dialog).direction === 'rtl';
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.#showLightbox(lb.index + (rtl ? -1 : 1));
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.#showLightbox(lb.index + (rtl ? 1 : -1));
        }
      },
      opts
    );

    // Follow the visible slide while swiping / dragging.
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(lb.raf);
        lb.raf = requestAnimationFrame(() => {
          if (lb.drag.active) return;
          const index = Math.round(Math.abs(track.scrollLeft) / (track.clientWidth || 1));
          if (index !== lb.index) {
            lb.index = Math.max(0, Math.min(lb.slides.length - 1, index));
            this.#updateLightbox();
          }
        });
      },
      { ...opts, passive: true }
    );

    // Mouse drag (touch and pen scroll natively).
    track.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (event.target instanceof Element && event.target.closest('button')) return;
        lb.drag = { active: true, moved: false, startX: event.clientX, startScroll: track.scrollLeft, pointerId: event.pointerId };
        track.style.scrollSnapType = 'none';
        track.style.cursor = 'grabbing';
      },
      opts
    );
    track.addEventListener(
      'pointermove',
      (event) => {
        if (!lb.drag.active || event.pointerId !== lb.drag.pointerId) return;
        const dx = event.clientX - lb.drag.startX;
        if (!lb.drag.moved && Math.abs(dx) > 4) {
          lb.drag.moved = true;
          track.setPointerCapture(event.pointerId);
        }
        if (lb.drag.moved) track.scrollLeft = lb.drag.startScroll - dx;
      },
      opts
    );
    /** @param {PointerEvent} event */
    const endDrag = (event) => {
      if (!lb.drag.active || event.pointerId !== lb.drag.pointerId) return;
      const dx = event.clientX - lb.drag.startX;
      lb.drag.active = false;
      if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
      track.style.cursor = '';
      if (!lb.drag.moved) {
        track.style.scrollSnapType = '';
        return;
      }
      let target = lb.index;
      const threshold = Math.min(80, track.clientWidth * 0.15);
      const rtl = getComputedStyle(track).direction === 'rtl';
      if (dx < -threshold) target += rtl ? -1 : 1;
      else if (dx > threshold) target += rtl ? 1 : -1;
      this.#showLightbox(Math.max(0, Math.min(lb.slides.length - 1, target)));
      window.setTimeout(() => {
        if (!lb.drag.active) track.style.scrollSnapType = '';
      }, 400);
    };
    track.addEventListener('pointerup', endDrag, opts);
    track.addEventListener('pointercancel', endDrag, opts);

    // A click on the empty area around the image (not after a drag) closes the lightbox.
    track.addEventListener(
      'click',
      (event) => {
        if (lb.drag.moved) {
          lb.drag.moved = false;
          return;
        }
        if (event.target instanceof HTMLElement && event.target.hasAttribute('data-lightbox-slide')) this.#closeLightbox();
      },
      opts
    );

    window.addEventListener('resize', () => dialog.open && this.#showLightbox(lb.index, 'instant'), opts);

    // On close: unlock page scroll and leave the page gallery on the image last viewed.
    dialog.addEventListener(
      'close',
      () => {
        document.documentElement.style.overflow = '';
        document.documentElement.style.scrollbarGutter = '';
        const media = lb.slides[lb.index]?.dataset.mediaId;
        const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === media);
        if (index !== -1 && index !== this.#index && this.#slides.length > 1) this.goTo(index, 'instant');
      },
      opts
    );
  }

  #openLightbox() {
    const { dialog, slides } = this.#lb;
    if (!dialog || !slides.length) return;
    // Open on the image currently shown in the page gallery (first image if a video is showing).
    const media = this.#slides[this.#index]?.dataset.mediaId;
    const start = Math.max(0, slides.findIndex((slide) => slide.dataset.mediaId === media));
    // Lock page scroll but keep the scrollbar's space reserved, so the page doesn't shift sideways.
    document.documentElement.style.scrollbarGutter = 'stable';
    document.documentElement.style.overflow = 'hidden';
    dialog.showModal();
    this.#animateLightbox(true);
    this.#showLightbox(start, 'instant');
  }

  /**
   * Scroll the lightbox to a slide. Past the last image goes to the first and vice versa (instant jump).
   * @param {number} index
   * @param {ScrollBehavior} [behavior]
   */
  #showLightbox(index, behavior) {
    const lb = this.#lb;
    if (!lb.track || !lb.slides.length) return;
    const count = lb.slides.length;
    const wrapped = index >= count || index < 0;
    const next = ((index % count) + count) % count;
    const slide = lb.slides[next];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    lb.track.scrollTo({
      left: slide.offsetLeft - lb.track.offsetLeft,
      behavior: behavior ?? (wrapped || reduce ? 'instant' : 'smooth'),
    });
    lb.index = next;
    this.#updateLightbox();
  }

  /** Active thumbnail for the current lightbox slide. */
  #updateLightbox() {
    const { dialog, thumbs, slides, index } = this.#lb;
    if (!dialog) return;
    thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    const rail = dialog.querySelector('[data-lightbox-thumbs]');
    const thumb = thumbs[index];
    if (rail instanceof HTMLElement && thumb && rail.scrollWidth > rail.clientWidth + 1) {
      const left = thumb.offsetLeft - rail.offsetLeft - (rail.clientWidth - thumb.offsetWidth) / 2;
      rail.scrollTo({ left, behavior: 'smooth' });
    }
  }

  #lbClosing = false;
  /** @type {Animation | null} */
  #lbBackdropAnimation = null;

  /** Close with a short fade + scale-down, then actually close the dialog. */
  #closeLightbox() {
    const { dialog } = this.#lb;
    if (!dialog?.open || this.#lbClosing) return;
    const animation = this.#animateLightbox(false);
    if (!animation) {
      dialog.close();
      return;
    }
    this.#lbClosing = true;
    animation.finished
      .catch(() => {})
      .then(() => {
        this.#lbClosing = false;
        dialog.close();
        // Drop the held "closed" frame so the next open starts from a clean state.
        animation.cancel();
        this.#lbBackdropAnimation?.cancel();
      });
  }

  /**
   * Fade + gentle scale for the card, fade for the dimmed backdrop. Returns the card animation,
   * or null when motion is reduced / unsupported (the dialog then opens and closes instantly).
   * @param {boolean} opening
   * @returns {Animation | null}
   */
  #animateLightbox(opening) {
    const { dialog } = this.#lb;
    if (!dialog || typeof dialog.animate !== 'function') return null;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
    const card = [
      { opacity: 0, transform: 'scale(0.96)' },
      { opacity: 1, transform: 'scale(1)' },
    ];
    const timing = opening
      ? { duration: 260, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      : { duration: 180, easing: 'cubic-bezier(0.4, 0, 1, 1)', direction: /** @type {PlaybackDirection} */ ('reverse'), fill: /** @type {FillMode} */ ('forwards') };
    const animation = dialog.animate(card, timing);
    try {
      this.#lbBackdropAnimation = dialog.animate([{ opacity: 0 }, { opacity: 1 }], { ...timing, pseudoElement: '::backdrop' });
    } catch {
      // Backdrop animation not supported: the card still animates.
    }
    return animation;
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
